// BankAdminDashboard.js - Partner Bank Administrator Dashboard
// Simplified to 5 tabs: overview, users_and_roles, interfaces_parameters, bank_profile, audit
// Uses zinc/monospace design system

import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Building2, Users, Plus, Eye, Edit, Ban, Trash2,
  RefreshCw, Search, CheckCircle, Clock, AlertTriangle,
  Activity, X, Save, Bell, Lock, Globe, Smartphone, Key,
  Settings, Shield, FileText, DollarSign,
} from 'lucide-react';
import { getStatusStyle } from '../../config/statusColors';
import useBankDashboardData from '../../hooks/useBankDashboardData';

// ============================================
// URL PATH TO TAB MAPPING
// ============================================
const getTabFromPath = (pathname) => {
  if (pathname.includes('/users')) return 'users_and_roles';
  if (pathname.includes('/interfaces')) return 'interfaces_parameters';
  if (pathname.includes('/profile')) return 'bank_profile';
  if (pathname.includes('/audit')) return 'audit';
  return 'overview';
};

// ============================================
// ROLE HELPERS
// ============================================
const getRoleColor = (role) => {
  const colors = {
    bank_admin: 'bg-purple-100 text-purple-700',
    bank_compliance_maker: 'bg-amber-100 text-amber-700',
    bank_compliance_checker: 'bg-yellow-100 text-yellow-700',
    bank_treasury_dealer: 'bg-blue-100 text-blue-700',
    bank_forwards_dealer: 'bg-indigo-100 text-indigo-700',
  };
  return colors[role] || 'bg-gray-100 text-gray-700';
};

const getRoleLabel = (role) => {
  const labels = {
    bank_admin: 'Bank Admin',
    bank_compliance_maker: 'Compliance Maker',
    bank_compliance_checker: 'Compliance Checker',
    bank_treasury_dealer: 'Treasury Dealer',
    bank_forwards_dealer: 'Forwards Dealer',
  };
  return labels[role] || role;
};

const AVAILABLE_ROLES = [
  'bank_compliance_maker',
  'bank_compliance_checker',
  'bank_treasury_dealer',
  'bank_forwards_dealer',
];

// Mutual exclusion pairs
const MUTUALLY_EXCLUSIVE = {
  bank_compliance_maker: 'bank_compliance_checker',
  bank_compliance_checker: 'bank_compliance_maker',
};

// ============================================
// AUDIT LOG MOCK DATA
// ============================================
const AUDIT_LOG = [
  { id: 1, timestamp: '2025-01-15T14:32:00Z', user: 'Sarah Ochieng', action: 'User Created', target: 'James Kamau', details: 'Assigned role: Compliance Maker' },
  { id: 2, timestamp: '2025-01-15T11:15:00Z', user: 'Peter Kimani', action: 'Role Changed', target: 'Alice Njeri', details: 'Changed from Treasury Dealer to Forwards Dealer' },
  { id: 3, timestamp: '2025-01-14T16:45:00Z', user: 'Sarah Ochieng', action: 'User Suspended', target: 'John Mwangi', details: 'Reason: Extended leave' },
  { id: 4, timestamp: '2025-01-14T09:00:00Z', user: 'System', action: 'CDS Signing Order Changed', target: 'Bank Profile', details: 'Changed to: Maker signs first, Checker signs second' },
  { id: 5, timestamp: '2025-01-13T15:30:00Z', user: 'Peter Kimani', action: 'Settings Updated', target: 'Notification Preferences', details: 'Disabled SMS notifications' },
  { id: 6, timestamp: '2025-01-13T10:20:00Z', user: 'Sarah Ochieng', action: 'API Key Generated', target: 'Interfaces', details: 'New API key created for external integration' },
  { id: 7, timestamp: '2025-01-12T14:00:00Z', user: 'System', action: 'User Login', target: 'Sarah Ochieng', details: 'IP: 196.201.xxx.xxx' },
  { id: 8, timestamp: '2025-01-12T08:45:00Z', user: 'Peter Kimani', action: 'Bank Profile Updated', target: 'Legal Entity', details: 'Updated registered address' },
];

// ============================================
// MODAL COMPONENT
// ============================================
const Modal = ({ isOpen, onClose, title, children, size = 'md' }) => {
  if (!isOpen) return null;
  const sizeClasses = { sm: 'max-w-md', md: 'max-w-2xl', lg: 'max-w-4xl', xl: 'max-w-6xl' };
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className={`relative bg-white rounded-none shadow-xl border border-zinc-200 ${sizeClasses[size]} w-full mx-4 max-h-[90vh] overflow-y-auto`}>
        <div className="flex items-center justify-between p-6 border-b border-zinc-200 sticky top-0 bg-white z-10">
          <h3 className="text-sm font-mono font-semibold text-zinc-900 uppercase tracking-wider">{title}</h3>
          <button onClick={onClose} className="p-2 hover:bg-zinc-100 rounded-none">
            <X className="w-5 h-5 text-zinc-500" />
          </button>
        </div>
        <div className="p-6">{children}</div>
      </div>
    </div>
  );
};

// ============================================
// TOGGLE COMPONENT
// ============================================
const Toggle = ({ enabled, onChange }) => (
  <button
    onClick={onChange}
    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${enabled ? 'bg-black' : 'bg-zinc-300'}`}
  >
    <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${enabled ? 'translate-x-6' : 'translate-x-1'}`} />
  </button>
);

// ============================================
// STATUS BADGE (using getStatusStyle)
// ============================================
const StatusBadge = ({ status }) => {
  const style = getStatusStyle(status);
  return (
    <span
      className="inline-flex items-center px-2 py-0.5 text-[10px] font-mono uppercase tracking-wider border rounded-none"
      style={{ ...style, borderColor: style.color }}
    >
      {status?.replace(/_/g, ' ')}
    </span>
  );
};

// ============================================
// FORMAT HELPERS
// ============================================
const formatDate = (dateStr) => {
  if (!dateStr) return 'Never';
  const date = new Date(dateStr);
  return date.toLocaleDateString('en-KE', { year: 'numeric', month: 'short', day: 'numeric' });
};

const formatTime = (dateStr) => {
  if (!dateStr) return 'Never';
  const date = new Date(dateStr);
  const now = new Date();
  const diff = now - date;
  if (diff < 3600000) return `${Math.floor(diff / 60000)} mins ago`;
  if (diff < 86400000) return `${Math.floor(diff / 3600000)} hours ago`;
  return formatDate(dateStr);
};

const formatDateTime = (d) => {
  if (!d) return '\u2014';
  const dt = new Date(d);
  return `${dt.toLocaleDateString('en-KE', { year: 'numeric', month: 'short', day: 'numeric' })} ${dt.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`;
};

// ============================================
// MAIN BANK ADMIN DASHBOARD
// ============================================
const BankAdminDashboard = () => {
  const location = useLocation();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  // Determine active tab from URL path
  const [activeTab, setActiveTab] = useState(() => getTabFromPath(location.pathname));

  // Update tab when URL changes
  useEffect(() => {
    setActiveTab(getTabFromPath(location.pathname));
  }, [location.pathname]);

  // ============================================
  // API DATA HOOK
  // ============================================
  const {
    loading: dataLoading,
    staff,
    setStaff,
    metrics,
    refresh: refreshData,
    addStaff: apiAddStaff,
    updateStaff: apiUpdateStaff,
    suspendStaff: apiSuspendStaff,
    deleteStaff: apiDeleteStaff,
  } = useBankDashboardData();

  // Modal states
  const [showAddStaffModal, setShowAddStaffModal] = useState(false);
  const [showEditStaffModal, setShowEditStaffModal] = useState(false);
  const [showDeleteConfirmModal, setShowDeleteConfirmModal] = useState(false);
  const [showStaffDetailsModal, setShowStaffDetailsModal] = useState(false);
  const [selectedStaff, setSelectedStaff] = useState(null);

  // Staff form data
  const [staffFormData, setStaffFormData] = useState({
    name: '', email: '', phone: '', role: 'bank_compliance_maker', department: 'Compliance'
  });

  // Settings state (for interfaces_parameters tab)
  const [settings, setSettings] = useState({
    timezone: 'Africa/Nairobi',
    currency: 'KES',
    twoFactorRequired: true,
    emailNotifications: true,
    smsNotifications: true,
    dailyReports: true,
    weeklyDigest: true,
    whatsappIntegration: true,
    mpesaIntegration: true,
    apiAccess: false,
  });

  // Bank profile state
  const [bankProfile, setBankProfile] = useState({
    legalName: 'Equity Bank Kenya Limited',
    tradingName: 'Equity Bank Kenya',
    registrationNumber: 'C.12345/2004',
    registeredAddress: '9th Floor, Equity Centre, Hospital Road',
    city: 'Nairobi',
    postalCode: '00100',
    country: 'Kenya',
    swiftCode: 'EABORBI',
    cdsSigningOrder: ['checker', 'maker'], // default: checker first
  });

  // Search filter for audit
  const [auditSearch, setAuditSearch] = useState('');

  // Origination fee settings
  const [originationSettings, setOriginationSettings] = useState({
    enabled: true,
    feePerAccount: 25,
    feeCurrency: 'USD',
  });
  const originationMetrics = {
    totalAccounts: 1247,
    mtdAccounts: 47,
    mtdRevenue: 1175,
    ytdRevenue: 31175,
  };

  // Use API loading state
  useEffect(() => {
    if (!dataLoading) {
      setLoading(false);
    }
  }, [dataLoading]);

  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      await refreshData();
    } finally {
      setRefreshing(false);
    }
  };

  // Handle tab change - update URL
  const handleTabChange = (tabId) => {
    setActiveTab(tabId);
    const tabToPath = {
      overview: '/bank/admin',
      users_and_roles: '/bank/admin/users',
      interfaces_parameters: '/bank/admin/interfaces',
      bank_profile: '/bank/admin/profile',
      audit: '/bank/admin/audit',
    };
    if (tabToPath[tabId]) {
      navigate(tabToPath[tabId]);
    }
  };

  // ============================================
  // STAFF MANAGEMENT FUNCTIONS (API-connected)
  // ============================================
  const handleAddStaff = async (e) => {
    e.preventDefault();
    try {
      await apiAddStaff(staffFormData);
      setShowAddStaffModal(false);
      setStaffFormData({ name: '', email: '', phone: '', role: 'bank_compliance_maker', department: 'Compliance' });
    } catch (err) {
      console.error('Failed to add staff:', err);
      alert('Failed to add staff member: ' + err.message);
    }
  };

  const handleEditStaff = async (e) => {
    e.preventDefault();
    try {
      await apiUpdateStaff(selectedStaff.id, staffFormData);
      setShowEditStaffModal(false);
      setSelectedStaff(null);
    } catch (err) {
      console.error('Failed to update staff:', err);
      alert('Failed to update staff member: ' + err.message);
    }
  };

  const handleSuspendStaff = async (member) => {
    try {
      await apiSuspendStaff(member.id);
    } catch (err) {
      console.error('Failed to suspend staff:', err);
      alert('Failed to update staff status: ' + err.message);
    }
  };

  const handleDeleteStaff = async () => {
    try {
      await apiDeleteStaff(selectedStaff.id);
      setShowDeleteConfirmModal(false);
      setSelectedStaff(null);
    } catch (err) {
      console.error('Failed to delete staff:', err);
      alert('Failed to delete staff member: ' + err.message);
    }
  };

  const openEditModal = (member) => {
    setSelectedStaff(member);
    setStaffFormData({
      name: member.name,
      email: member.email,
      phone: member.phone || '',
      role: member.role,
      department: member.department || 'Compliance',
    });
    setShowEditStaffModal(true);
  };

  const openDeleteModal = (member) => {
    setSelectedStaff(member);
    setShowDeleteConfirmModal(true);
  };

  const openDetailsModal = (member) => {
    setSelectedStaff(member);
    setShowStaffDetailsModal(true);
  };

  // Settings update
  const updateSetting = (key, value) => {
    setSettings(prev => ({ ...prev, [key]: value }));
  };

  // Bank profile update
  const updateBankProfile = (key, value) => {
    setBankProfile(prev => ({ ...prev, [key]: value }));
  };

  // Toggle CDS signing order
  const toggleCdsSigningOrder = () => {
    setBankProfile(prev => ({
      ...prev,
      cdsSigningOrder: prev.cdsSigningOrder[0] === 'checker'
        ? ['maker', 'checker']
        : ['checker', 'maker'],
    }));
  };

  // Derived counts for overview
  const activeUsers = staff.filter(s => s.status === 'active').length;
  const pendingKycCount = 3; // mock - pending KYC reviews
  const systemHealthy = true;

  // Filter audit log
  const filteredAudit = AUDIT_LOG.filter(entry => {
    if (!auditSearch) return true;
    const q = auditSearch.toLowerCase();
    return (
      entry.user.toLowerCase().includes(q) ||
      entry.action.toLowerCase().includes(q) ||
      entry.target.toLowerCase().includes(q) ||
      entry.details.toLowerCase().includes(q)
    );
  });

  // Role form helper: determine which roles should be disabled based on mutual exclusion
  const getDisabledRole = (currentRole) => {
    return MUTUALLY_EXCLUSIVE[currentRole] || null;
  };

  // ============================================
  // LOADING STATE
  // ============================================
  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-zinc-800 border-t-transparent rounded-full animate-spin mx-auto" />
          <p className="text-zinc-600 font-mono text-sm mt-4">Loading bank dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 bg-zinc-50 min-h-screen">
      {/* Header Banner */}
      <div className="bg-black rounded-none p-6 text-white mb-6 border border-zinc-800">
        <div className="flex items-center justify-between">
          <div>
            <div className="flex items-center gap-2 mb-2">
              <Building2 className="w-5 h-5 text-zinc-400" />
              <span className="text-zinc-400 text-[10px] font-mono uppercase tracking-widest">Bank Administration</span>
            </div>
            <h1 className="text-xl font-mono font-bold tracking-tight">{bankProfile.tradingName}</h1>
            <p className="text-zinc-400 font-mono text-xs mt-1">
              {activeUsers} active users &middot; {pendingKycCount} pending KYC &middot; System {systemHealthy ? 'healthy' : 'degraded'}
            </p>
          </div>
          <div className="text-right">
            <p className="text-[10px] font-mono uppercase tracking-widest text-zinc-400">Active Staff</p>
            <p className="text-3xl font-mono font-bold">{activeUsers}</p>
            <p className="text-[10px] font-mono text-zinc-400 mt-1">{staff.length} total registered</p>
          </div>
        </div>
      </div>

      {/* Navigation Tabs - zinc/monospace design */}
      <div className="bg-white rounded-none border border-zinc-200 p-1 flex flex-wrap gap-1 mb-6">
        {[
          { id: 'overview', label: 'Overview', icon: Activity },
          { id: 'users_and_roles', label: 'Users & Roles', icon: Users },
          { id: 'interfaces_parameters', label: 'Interfaces & Parameters', icon: Settings },
          { id: 'bank_profile', label: 'Bank Profile', icon: Building2 },
          { id: 'audit', label: 'Audit Trail', icon: FileText },
        ].map(tab => (
          <button
            key={tab.id}
            onClick={() => handleTabChange(tab.id)}
            className={`flex items-center gap-2 px-4 py-2 font-mono text-xs uppercase tracking-wider transition-colors whitespace-nowrap ${
              activeTab === tab.id
                ? 'bg-black text-white'
                : 'bg-white text-zinc-500 hover:bg-zinc-50'
            }`}
          >
            <tab.icon className="w-3.5 h-3.5" />
            {tab.label}
          </button>
        ))}
        <div className="flex-1" />
        <button
          onClick={handleRefresh}
          disabled={refreshing}
          className="flex items-center gap-2 px-4 py-2 text-zinc-500 hover:bg-zinc-50 font-mono text-xs uppercase tracking-wider"
        >
          <RefreshCw className={`w-3.5 h-3.5 ${refreshing ? 'animate-spin' : ''}`} />
          Refresh
        </button>
      </div>

      {/* ============================================ */}
      {/* OVERVIEW TAB */}
      {/* ============================================ */}
      {activeTab === 'overview' && (
        <div className="space-y-6">
          {/* KPI Cards */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {/* Active Users */}
            <div className="bg-white rounded-none border border-zinc-200 p-5">
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-[10px] font-mono uppercase tracking-widest text-zinc-500">Active Users</p>
                  <p className="text-2xl font-mono font-bold text-zinc-900 mt-1">{activeUsers}</p>
                  <p className="text-xs font-mono text-zinc-400 mt-1">{staff.length} total registered</p>
                </div>
                <div className="p-2 bg-zinc-100">
                  <Users className="w-5 h-5 text-zinc-600" />
                </div>
              </div>
            </div>

            {/* Pending KYC */}
            <div className="bg-white rounded-none border border-zinc-200 p-5">
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-[10px] font-mono uppercase tracking-widest text-zinc-500">Pending KYC</p>
                  <p className="text-2xl font-mono font-bold text-zinc-900 mt-1">{pendingKycCount}</p>
                  <p className="text-xs font-mono text-zinc-400 mt-1">Awaiting review</p>
                </div>
                <div className="p-2 bg-zinc-100">
                  <Clock className="w-5 h-5 text-zinc-600" />
                </div>
              </div>
            </div>

            {/* System Status */}
            <div className="bg-white rounded-none border border-zinc-200 p-5">
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-[10px] font-mono uppercase tracking-widest text-zinc-500">System Status</p>
                  <p className="text-2xl font-mono font-bold text-zinc-900 mt-1">{systemHealthy ? 'Healthy' : 'Degraded'}</p>
                  <p className="text-xs font-mono text-zinc-400 mt-1">All services operational</p>
                </div>
                <div className="p-2 bg-zinc-100">
                  <CheckCircle className="w-5 h-5 text-zinc-600" />
                </div>
              </div>
            </div>

            {/* Origination Revenue */}
            <div className="bg-white rounded-none border border-zinc-200 p-5">
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-[10px] font-mono uppercase tracking-widest text-zinc-500">Origination Revenue</p>
                  <p className="text-2xl font-mono font-bold text-zinc-900 mt-1">${originationMetrics.mtdRevenue.toLocaleString()}</p>
                  <p className="text-xs font-mono text-zinc-400 mt-1">{originationMetrics.mtdAccounts} accounts MTD</p>
                </div>
                <div className="p-2 bg-zinc-100">
                  <DollarSign className="w-5 h-5 text-zinc-600" />
                </div>
              </div>
            </div>
          </div>

          {/* Role Breakdown */}
          <div className="bg-white rounded-none border border-zinc-200">
            <div className="p-4 border-b border-zinc-200">
              <h3 className="text-xs font-mono font-semibold text-zinc-900 uppercase tracking-wider">Users by Role</h3>
            </div>
            <div className="p-4">
              <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                {['bank_admin', 'bank_compliance_maker', 'bank_compliance_checker', 'bank_treasury_dealer', 'bank_forwards_dealer'].map(role => {
                  const count = staff.filter(s => s.role === role && s.status === 'active').length;
                  return (
                    <div key={role} className="p-3 border border-zinc-200 rounded-none">
                      <span className={`inline-block px-2 py-0.5 text-[10px] font-mono uppercase tracking-wider rounded-none ${getRoleColor(role)}`}>
                        {getRoleLabel(role)}
                      </span>
                      <p className="text-lg font-mono font-bold text-zinc-900 mt-2">{count}</p>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>

          {/* Recent Audit Activity */}
          <div className="bg-white rounded-none border border-zinc-200">
            <div className="p-4 border-b border-zinc-200 flex items-center justify-between">
              <h3 className="text-xs font-mono font-semibold text-zinc-900 uppercase tracking-wider">Recent Activity</h3>
              <button
                onClick={() => handleTabChange('audit')}
                className="text-xs font-mono text-zinc-500 hover:text-zinc-900 uppercase tracking-wider"
              >
                View All
              </button>
            </div>
            <div className="divide-y divide-zinc-100">
              {AUDIT_LOG.slice(0, 4).map(entry => (
                <div key={entry.id} className="px-4 py-3 flex items-center gap-4">
                  <p className="text-[10px] font-mono text-zinc-400 w-28 flex-shrink-0">
                    {formatDateTime(entry.timestamp)}
                  </p>
                  <p className="text-xs font-mono text-zinc-900 font-medium w-28 flex-shrink-0">{entry.user}</p>
                  <p className="text-xs font-mono text-zinc-700 flex-1">{entry.action} &mdash; {entry.target}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* ============================================ */}
      {/* USERS & ROLES TAB */}
      {/* ============================================ */}
      {activeTab === 'users_and_roles' && (
        <div className="space-y-6">
          <div className="bg-white rounded-none border border-zinc-200 overflow-hidden">
            <div className="p-4 border-b border-zinc-200">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-xs font-mono font-semibold text-zinc-900 uppercase tracking-wider">Users & Roles</h3>
                  <p className="text-xs font-mono text-zinc-500 mt-1">Manage bank staff members and their access roles</p>
                </div>
                <button
                  onClick={() => {
                    setStaffFormData({ name: '', email: '', phone: '', role: 'bank_compliance_maker', department: 'Compliance' });
                    setShowAddStaffModal(true);
                  }}
                  className="flex items-center gap-2 px-4 py-2 bg-black text-white font-mono text-xs uppercase tracking-wider hover:bg-zinc-800"
                >
                  <Plus className="w-3.5 h-3.5" />
                  Add User
                </button>
              </div>
            </div>

            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-zinc-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-[10px] font-mono font-medium text-zinc-500 uppercase tracking-wider">User</th>
                    <th className="px-4 py-3 text-left text-[10px] font-mono font-medium text-zinc-500 uppercase tracking-wider">Role</th>
                    <th className="px-4 py-3 text-left text-[10px] font-mono font-medium text-zinc-500 uppercase tracking-wider">Department</th>
                    <th className="px-4 py-3 text-left text-[10px] font-mono font-medium text-zinc-500 uppercase tracking-wider">Status</th>
                    <th className="px-4 py-3 text-left text-[10px] font-mono font-medium text-zinc-500 uppercase tracking-wider">Last Login</th>
                    <th className="px-4 py-3 text-left text-[10px] font-mono font-medium text-zinc-500 uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-zinc-100">
                  {staff.map(member => (
                    <tr key={member.id} className="hover:bg-zinc-50">
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 bg-zinc-200 rounded-none flex items-center justify-center">
                            <span className="text-zinc-600 font-mono text-xs font-medium">
                              {member.name.split(' ').map(n => n[0]).join('')}
                            </span>
                          </div>
                          <div>
                            <p className="font-mono text-xs font-medium text-zinc-900">{member.name}</p>
                            <p className="text-[10px] font-mono text-zinc-400">{member.email}</p>
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <span className={`inline-block px-2 py-0.5 text-[10px] font-mono uppercase tracking-wider rounded-none ${getRoleColor(member.role)}`}>
                          {getRoleLabel(member.role)}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-xs font-mono text-zinc-600">{member.department}</td>
                      <td className="px-4 py-3">
                        <StatusBadge status={member.status} />
                      </td>
                      <td className="px-4 py-3 text-xs font-mono text-zinc-500">{formatTime(member.lastLogin)}</td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-1">
                          <button onClick={() => openDetailsModal(member)} className="p-1.5 text-zinc-500 hover:bg-zinc-100 rounded-none" title="View Details">
                            <Eye className="w-3.5 h-3.5" />
                          </button>
                          <button onClick={() => openEditModal(member)} className="p-1.5 text-zinc-500 hover:bg-zinc-100 rounded-none" title="Edit">
                            <Edit className="w-3.5 h-3.5" />
                          </button>
                          <button
                            onClick={() => handleSuspendStaff(member)}
                            className="p-1.5 text-zinc-500 hover:bg-zinc-100 rounded-none"
                            title={member.status === 'suspended' ? 'Activate' : 'Suspend'}
                          >
                            <Ban className="w-3.5 h-3.5" />
                          </button>
                          <button onClick={() => openDeleteModal(member)} className="p-1.5 text-red-500 hover:bg-red-50 rounded-none" title="Delete">
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* ============================================ */}
      {/* INTERFACES & PARAMETERS TAB */}
      {/* ============================================ */}
      {activeTab === 'interfaces_parameters' && (
        <div className="space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* System Configuration */}
            <div className="bg-white rounded-none border border-zinc-200 overflow-hidden">
              <div className="p-4 bg-zinc-50 border-b border-zinc-200 flex items-center gap-3">
                <Settings className="w-4 h-4 text-zinc-600" />
                <h3 className="text-xs font-mono font-semibold text-zinc-900 uppercase tracking-wider">System Configuration</h3>
              </div>
              <div className="p-6 space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-[10px] font-mono font-medium text-zinc-500 uppercase tracking-wider mb-1">Timezone</label>
                    <select
                      value={settings.timezone}
                      onChange={(e) => updateSetting('timezone', e.target.value)}
                      className="w-full px-3 py-2 border border-zinc-200 rounded-none font-mono text-xs text-zinc-900 bg-white"
                    >
                      <option value="Africa/Nairobi">Africa/Nairobi (EAT)</option>
                      <option value="UTC">UTC</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-[10px] font-mono font-medium text-zinc-500 uppercase tracking-wider mb-1">Currency</label>
                    <select
                      value={settings.currency}
                      onChange={(e) => updateSetting('currency', e.target.value)}
                      className="w-full px-3 py-2 border border-zinc-200 rounded-none font-mono text-xs text-zinc-900 bg-white"
                    >
                      <option value="KES">KES (Kenya Shilling)</option>
                      <option value="TZS">TZS (Tanzania Shilling)</option>
                      <option value="UGX">UGX (Uganda Shilling)</option>
                    </select>
                  </div>
                </div>
              </div>
            </div>

            {/* Security / API Keys */}
            <div className="bg-white rounded-none border border-zinc-200 overflow-hidden">
              <div className="p-4 bg-zinc-50 border-b border-zinc-200 flex items-center gap-3">
                <Lock className="w-4 h-4 text-zinc-600" />
                <h3 className="text-xs font-mono font-semibold text-zinc-900 uppercase tracking-wider">Security & API Keys</h3>
              </div>
              <div className="p-6 space-y-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-mono text-xs font-medium text-zinc-900">Two-Factor Authentication</p>
                    <p className="text-[10px] font-mono text-zinc-500">Require 2FA for all staff logins</p>
                  </div>
                  <Toggle enabled={settings.twoFactorRequired} onChange={() => updateSetting('twoFactorRequired', !settings.twoFactorRequired)} />
                </div>
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-mono text-xs font-medium text-zinc-900">API Access</p>
                    <p className="text-[10px] font-mono text-zinc-500">Enable external API integrations</p>
                  </div>
                  <Toggle enabled={settings.apiAccess} onChange={() => updateSetting('apiAccess', !settings.apiAccess)} />
                </div>
                {settings.apiAccess && (
                  <div className="p-3 border border-zinc-200 bg-zinc-50">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <Key className="w-3.5 h-3.5 text-zinc-500" />
                        <span className="font-mono text-xs text-zinc-700">API Key</span>
                      </div>
                      <button className="font-mono text-[10px] text-zinc-600 hover:text-zinc-900 uppercase tracking-wider border border-zinc-300 px-2 py-1">
                        Generate New
                      </button>
                    </div>
                    <p className="font-mono text-[10px] text-zinc-400 mt-2">ff_live_*****************************k3mJ</p>
                  </div>
                )}
              </div>
            </div>

            {/* Notification Preferences */}
            <div className="bg-white rounded-none border border-zinc-200 overflow-hidden">
              <div className="p-4 bg-zinc-50 border-b border-zinc-200 flex items-center gap-3">
                <Bell className="w-4 h-4 text-zinc-600" />
                <h3 className="text-xs font-mono font-semibold text-zinc-900 uppercase tracking-wider">Notification Preferences</h3>
              </div>
              <div className="p-6 space-y-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-mono text-xs font-medium text-zinc-900">Email Notifications</p>
                    <p className="text-[10px] font-mono text-zinc-500">Receive alerts via email</p>
                  </div>
                  <Toggle enabled={settings.emailNotifications} onChange={() => updateSetting('emailNotifications', !settings.emailNotifications)} />
                </div>
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-mono text-xs font-medium text-zinc-900">SMS Notifications</p>
                    <p className="text-[10px] font-mono text-zinc-500">Receive alerts via SMS</p>
                  </div>
                  <Toggle enabled={settings.smsNotifications} onChange={() => updateSetting('smsNotifications', !settings.smsNotifications)} />
                </div>
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-mono text-xs font-medium text-zinc-900">Daily Reports</p>
                    <p className="text-[10px] font-mono text-zinc-500">Receive daily summary reports</p>
                  </div>
                  <Toggle enabled={settings.dailyReports} onChange={() => updateSetting('dailyReports', !settings.dailyReports)} />
                </div>
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-mono text-xs font-medium text-zinc-900">Weekly Digest</p>
                    <p className="text-[10px] font-mono text-zinc-500">Receive weekly performance digest</p>
                  </div>
                  <Toggle enabled={settings.weeklyDigest} onChange={() => updateSetting('weeklyDigest', !settings.weeklyDigest)} />
                </div>
              </div>
            </div>

            {/* Integration Settings */}
            <div className="bg-white rounded-none border border-zinc-200 overflow-hidden">
              <div className="p-4 bg-zinc-50 border-b border-zinc-200 flex items-center gap-3">
                <Globe className="w-4 h-4 text-zinc-600" />
                <h3 className="text-xs font-mono font-semibold text-zinc-900 uppercase tracking-wider">Integrations</h3>
              </div>
              <div className="p-6 space-y-4">
                <div className="p-3 border border-zinc-200">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <Smartphone className="w-4 h-4 text-zinc-600" />
                      <span className="font-mono text-xs font-medium text-zinc-900">WhatsApp Business</span>
                    </div>
                    <Toggle enabled={settings.whatsappIntegration} onChange={() => updateSetting('whatsappIntegration', !settings.whatsappIntegration)} />
                  </div>
                  <p className="text-[10px] font-mono text-zinc-500">WhatsApp Business messaging for client notifications</p>
                </div>
                <div className="p-3 border border-zinc-200">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <Activity className="w-4 h-4 text-zinc-600" />
                      <span className="font-mono text-xs font-medium text-zinc-900">M-Pesa</span>
                    </div>
                    <Toggle enabled={settings.mpesaIntegration} onChange={() => updateSetting('mpesaIntegration', !settings.mpesaIntegration)} />
                  </div>
                  <p className="text-[10px] font-mono text-zinc-500">Mobile money payment integration</p>
                </div>
              </div>
            </div>
          </div>

          {/* Account Origination Fees */}
          <div className="bg-white rounded-none border border-zinc-200 overflow-hidden">
            <div className="p-4 bg-zinc-50 border-b border-zinc-200 flex items-center gap-3">
              <DollarSign className="w-4 h-4 text-zinc-600" />
              <h3 className="text-xs font-mono font-semibold text-zinc-900 uppercase tracking-wider">Account Origination Fees</h3>
            </div>
            <div className="p-6 space-y-6">
              {/* Controls row */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="flex items-center justify-between md:col-span-1">
                  <div>
                    <p className="font-mono text-xs font-medium text-zinc-900">Enable Origination Fee</p>
                    <p className="text-[10px] font-mono text-zinc-500">Charge fee per new account</p>
                  </div>
                  <button
                    onClick={() => setOriginationSettings(prev => ({ ...prev, enabled: !prev.enabled }))}
                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${originationSettings.enabled ? 'bg-black' : 'bg-zinc-300'}`}
                  >
                    <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${originationSettings.enabled ? 'translate-x-6' : 'translate-x-1'}`} />
                  </button>
                </div>
                <div>
                  <label className="block text-[10px] font-mono font-medium text-zinc-500 uppercase tracking-wider mb-1">Fee per Account</label>
                  <input
                    type="number"
                    value={originationSettings.feePerAccount}
                    onChange={e => setOriginationSettings(prev => ({ ...prev, feePerAccount: Number(e.target.value) }))}
                    className="w-full px-3 py-2 border border-zinc-200 rounded-none font-mono text-xs text-zinc-900 bg-white"
                  />
                </div>
                <div>
                  <label className="block text-[10px] font-mono font-medium text-zinc-500 uppercase tracking-wider mb-1">Fee Currency</label>
                  <select
                    value={originationSettings.feeCurrency}
                    onChange={e => setOriginationSettings(prev => ({ ...prev, feeCurrency: e.target.value }))}
                    className="w-full px-3 py-2 border border-zinc-200 rounded-none font-mono text-xs text-zinc-900 bg-white"
                  >
                    <option value="USD">USD</option>
                    <option value="EUR">EUR</option>
                    <option value="GBP">GBP</option>
                  </select>
                </div>
              </div>

              {/* Summary table */}
              <div className="border border-zinc-200 rounded-none overflow-hidden">
                <table className="w-full font-mono text-xs">
                  <thead>
                    <tr className="bg-zinc-50 border-b border-zinc-200">
                      <th className="px-4 py-2.5 text-left text-[10px] uppercase tracking-wider text-zinc-500 font-medium">Metric</th>
                      <th className="px-4 py-2.5 text-right text-[10px] uppercase tracking-wider text-zinc-500 font-medium">Value</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-zinc-100">
                    <tr>
                      <td className="px-4 py-2.5 text-zinc-700">Total Accounts Originated</td>
                      <td className="px-4 py-2.5 text-right font-bold text-zinc-900">{originationMetrics.totalAccounts.toLocaleString()}</td>
                    </tr>
                    <tr>
                      <td className="px-4 py-2.5 text-zinc-700">MTD Accounts</td>
                      <td className="px-4 py-2.5 text-right font-bold text-zinc-900">{originationMetrics.mtdAccounts}</td>
                    </tr>
                    <tr>
                      <td className="px-4 py-2.5 text-zinc-700">MTD Revenue</td>
                      <td className="px-4 py-2.5 text-right font-bold text-zinc-900">${originationMetrics.mtdRevenue.toLocaleString()}</td>
                    </tr>
                    <tr>
                      <td className="px-4 py-2.5 text-zinc-700">YTD Revenue</td>
                      <td className="px-4 py-2.5 text-right font-bold text-zinc-900">${originationMetrics.ytdRevenue.toLocaleString()}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          {/* Save Button */}
          <div className="flex justify-end">
            <button className="flex items-center gap-2 px-6 py-2 bg-black text-white font-mono text-xs uppercase tracking-wider hover:bg-zinc-800">
              <Save className="w-3.5 h-3.5" />
              Save Settings
            </button>
          </div>
        </div>
      )}

      {/* ============================================ */}
      {/* BANK PROFILE TAB */}
      {/* ============================================ */}
      {activeTab === 'bank_profile' && (
        <div className="space-y-6">
          {/* Legal Entity Info */}
          <div className="bg-white rounded-none border border-zinc-200 overflow-hidden">
            <div className="p-4 bg-zinc-50 border-b border-zinc-200 flex items-center gap-3">
              <Building2 className="w-4 h-4 text-zinc-600" />
              <h3 className="text-xs font-mono font-semibold text-zinc-900 uppercase tracking-wider">Legal Entity Information</h3>
            </div>
            <div className="p-6 space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-[10px] font-mono font-medium text-zinc-500 uppercase tracking-wider mb-1">Legal Name</label>
                  <input
                    type="text"
                    value={bankProfile.legalName}
                    onChange={(e) => updateBankProfile('legalName', e.target.value)}
                    className="w-full px-3 py-2 border border-zinc-200 rounded-none font-mono text-xs text-zinc-900"
                  />
                </div>
                <div>
                  <label className="block text-[10px] font-mono font-medium text-zinc-500 uppercase tracking-wider mb-1">Trading Name</label>
                  <input
                    type="text"
                    value={bankProfile.tradingName}
                    onChange={(e) => updateBankProfile('tradingName', e.target.value)}
                    className="w-full px-3 py-2 border border-zinc-200 rounded-none font-mono text-xs text-zinc-900"
                  />
                </div>
                <div>
                  <label className="block text-[10px] font-mono font-medium text-zinc-500 uppercase tracking-wider mb-1">Registration Number</label>
                  <input
                    type="text"
                    value={bankProfile.registrationNumber}
                    onChange={(e) => updateBankProfile('registrationNumber', e.target.value)}
                    className="w-full px-3 py-2 border border-zinc-200 rounded-none font-mono text-xs text-zinc-900"
                  />
                </div>
                <div>
                  <label className="block text-[10px] font-mono font-medium text-zinc-500 uppercase tracking-wider mb-1">SWIFT / BIC Code</label>
                  <input
                    type="text"
                    value={bankProfile.swiftCode}
                    onChange={(e) => updateBankProfile('swiftCode', e.target.value)}
                    className="w-full px-3 py-2 border border-zinc-200 rounded-none font-mono text-xs text-zinc-900"
                  />
                </div>
              </div>

              <div>
                <label className="block text-[10px] font-mono font-medium text-zinc-500 uppercase tracking-wider mb-1">Registered Address</label>
                <input
                  type="text"
                  value={bankProfile.registeredAddress}
                  onChange={(e) => updateBankProfile('registeredAddress', e.target.value)}
                  className="w-full px-3 py-2 border border-zinc-200 rounded-none font-mono text-xs text-zinc-900"
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-[10px] font-mono font-medium text-zinc-500 uppercase tracking-wider mb-1">City</label>
                  <input
                    type="text"
                    value={bankProfile.city}
                    onChange={(e) => updateBankProfile('city', e.target.value)}
                    className="w-full px-3 py-2 border border-zinc-200 rounded-none font-mono text-xs text-zinc-900"
                  />
                </div>
                <div>
                  <label className="block text-[10px] font-mono font-medium text-zinc-500 uppercase tracking-wider mb-1">Postal Code</label>
                  <input
                    type="text"
                    value={bankProfile.postalCode}
                    onChange={(e) => updateBankProfile('postalCode', e.target.value)}
                    className="w-full px-3 py-2 border border-zinc-200 rounded-none font-mono text-xs text-zinc-900"
                  />
                </div>
                <div>
                  <label className="block text-[10px] font-mono font-medium text-zinc-500 uppercase tracking-wider mb-1">Country</label>
                  <input
                    type="text"
                    value={bankProfile.country}
                    onChange={(e) => updateBankProfile('country', e.target.value)}
                    className="w-full px-3 py-2 border border-zinc-200 rounded-none font-mono text-xs text-zinc-900"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* CDS Signing Order */}
          <div className="bg-white rounded-none border border-zinc-200 overflow-hidden">
            <div className="p-4 bg-zinc-50 border-b border-zinc-200 flex items-center gap-3">
              <Shield className="w-4 h-4 text-zinc-600" />
              <h3 className="text-xs font-mono font-semibold text-zinc-900 uppercase tracking-wider">CDS Signing Order</h3>
            </div>
            <div className="p-6">
              <p className="text-xs font-mono text-zinc-600 mb-4">
                Configure the order in which Compliance Maker and Compliance Checker sign CDS cards. This affects the signature workflow for all new CDS card submissions.
              </p>

              <div className="flex items-center gap-6">
                <button
                  onClick={() => bankProfile.cdsSigningOrder[0] !== 'checker' && toggleCdsSigningOrder()}
                  className={`flex-1 p-4 border text-left ${
                    bankProfile.cdsSigningOrder[0] === 'checker'
                      ? 'border-black bg-zinc-50'
                      : 'border-zinc-200 hover:border-zinc-300'
                  }`}
                >
                  <div className="flex items-center gap-2 mb-2">
                    <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center ${
                      bankProfile.cdsSigningOrder[0] === 'checker' ? 'border-black' : 'border-zinc-300'
                    }`}>
                      {bankProfile.cdsSigningOrder[0] === 'checker' && <div className="w-2 h-2 rounded-full bg-black" />}
                    </div>
                    <span className="text-xs font-mono font-medium text-zinc-900 uppercase tracking-wider">Default Order</span>
                  </div>
                  <p className="text-[10px] font-mono text-zinc-500 ml-6">
                    1st: Checker signs &rarr; 2nd: Maker signs
                  </p>
                </button>

                <button
                  onClick={() => bankProfile.cdsSigningOrder[0] !== 'maker' && toggleCdsSigningOrder()}
                  className={`flex-1 p-4 border text-left ${
                    bankProfile.cdsSigningOrder[0] === 'maker'
                      ? 'border-black bg-zinc-50'
                      : 'border-zinc-200 hover:border-zinc-300'
                  }`}
                >
                  <div className="flex items-center gap-2 mb-2">
                    <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center ${
                      bankProfile.cdsSigningOrder[0] === 'maker' ? 'border-black' : 'border-zinc-300'
                    }`}>
                      {bankProfile.cdsSigningOrder[0] === 'maker' && <div className="w-2 h-2 rounded-full bg-black" />}
                    </div>
                    <span className="text-xs font-mono font-medium text-zinc-900 uppercase tracking-wider">Alternative Order</span>
                  </div>
                  <p className="text-[10px] font-mono text-zinc-500 ml-6">
                    1st: Maker signs &rarr; 2nd: Checker signs
                  </p>
                </button>
              </div>

              <p className="text-[10px] font-mono text-zinc-400 mt-4">
                Current order: {bankProfile.cdsSigningOrder[0] === 'checker' ? 'Checker' : 'Maker'} signs first (1st signature), {bankProfile.cdsSigningOrder[1] === 'maker' ? 'Maker' : 'Checker'} signs second (2nd signature)
              </p>
            </div>
          </div>

          {/* Save Button */}
          <div className="flex justify-end">
            <button className="flex items-center gap-2 px-6 py-2 bg-black text-white font-mono text-xs uppercase tracking-wider hover:bg-zinc-800">
              <Save className="w-3.5 h-3.5" />
              Save Profile
            </button>
          </div>
        </div>
      )}

      {/* ============================================ */}
      {/* AUDIT TAB */}
      {/* ============================================ */}
      {activeTab === 'audit' && (
        <div className="space-y-6">
          <div className="bg-white rounded-none border border-zinc-200 overflow-hidden">
            <div className="p-4 border-b border-zinc-200">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-xs font-mono font-semibold text-zinc-900 uppercase tracking-wider">Audit Trail</h3>
                  <p className="text-xs font-mono text-zinc-500 mt-1">Complete log of administrative actions</p>
                </div>
                <div className="flex items-center gap-2">
                  <div className="relative">
                    <Search className="w-3.5 h-3.5 text-zinc-400 absolute left-3 top-1/2 -translate-y-1/2" />
                    <input
                      type="text"
                      placeholder="Search audit log..."
                      value={auditSearch}
                      onChange={(e) => setAuditSearch(e.target.value)}
                      className="pl-9 pr-3 py-2 border border-zinc-200 rounded-none font-mono text-xs text-zinc-900 w-64"
                    />
                  </div>
                </div>
              </div>
            </div>

            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-zinc-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-[10px] font-mono font-medium text-zinc-500 uppercase tracking-wider">Timestamp</th>
                    <th className="px-4 py-3 text-left text-[10px] font-mono font-medium text-zinc-500 uppercase tracking-wider">User</th>
                    <th className="px-4 py-3 text-left text-[10px] font-mono font-medium text-zinc-500 uppercase tracking-wider">Action</th>
                    <th className="px-4 py-3 text-left text-[10px] font-mono font-medium text-zinc-500 uppercase tracking-wider">Target</th>
                    <th className="px-4 py-3 text-left text-[10px] font-mono font-medium text-zinc-500 uppercase tracking-wider">Details</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-zinc-100">
                  {filteredAudit.map(entry => (
                    <tr key={entry.id} className="hover:bg-zinc-50">
                      <td className="px-4 py-3 text-xs font-mono text-zinc-500 whitespace-nowrap">
                        {formatDateTime(entry.timestamp)}
                      </td>
                      <td className="px-4 py-3">
                        <span className={`font-mono text-xs font-medium ${entry.user === 'System' ? 'text-zinc-400' : 'text-zinc-900'}`}>
                          {entry.user}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <span className="inline-flex items-center px-2 py-0.5 text-[10px] font-mono uppercase tracking-wider bg-zinc-100 text-zinc-700 border border-zinc-200 rounded-none">
                          {entry.action}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-xs font-mono text-zinc-700">{entry.target}</td>
                      <td className="px-4 py-3 text-xs font-mono text-zinc-500">{entry.details}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {filteredAudit.length === 0 && (
              <div className="p-8 text-center">
                <p className="text-xs font-mono text-zinc-400">No audit entries match your search.</p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ============================================ */}
      {/* MODALS */}
      {/* ============================================ */}

      {/* Add Staff Modal */}
      <Modal isOpen={showAddStaffModal} onClose={() => setShowAddStaffModal(false)} title="Add New User">
        <form onSubmit={handleAddStaff} className="space-y-4">
          <div>
            <label className="block text-[10px] font-mono font-medium text-zinc-500 uppercase tracking-wider mb-1">Full Name</label>
            <input
              type="text"
              required
              value={staffFormData.name}
              onChange={(e) => setStaffFormData({ ...staffFormData, name: e.target.value })}
              className="w-full px-3 py-2 border border-zinc-200 rounded-none font-mono text-xs"
              placeholder="Enter full name"
            />
          </div>
          <div>
            <label className="block text-[10px] font-mono font-medium text-zinc-500 uppercase tracking-wider mb-1">Email</label>
            <input
              type="email"
              required
              value={staffFormData.email}
              onChange={(e) => setStaffFormData({ ...staffFormData, email: e.target.value })}
              className="w-full px-3 py-2 border border-zinc-200 rounded-none font-mono text-xs"
              placeholder="user@bank.com"
            />
          </div>
          <div>
            <label className="block text-[10px] font-mono font-medium text-zinc-500 uppercase tracking-wider mb-1">Phone</label>
            <input
              type="tel"
              value={staffFormData.phone}
              onChange={(e) => setStaffFormData({ ...staffFormData, phone: e.target.value })}
              className="w-full px-3 py-2 border border-zinc-200 rounded-none font-mono text-xs"
              placeholder="+254 7XX XXX XXX"
            />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-[10px] font-mono font-medium text-zinc-500 uppercase tracking-wider mb-1">Role</label>
              <select
                value={staffFormData.role}
                onChange={(e) => setStaffFormData({ ...staffFormData, role: e.target.value })}
                className="w-full px-3 py-2 border border-zinc-200 rounded-none font-mono text-xs bg-white"
              >
                {AVAILABLE_ROLES.map(role => {
                  const disabled = getDisabledRole(staffFormData.role) === role;
                  return (
                    <option key={role} value={role} disabled={disabled}>
                      {getRoleLabel(role)}{disabled ? ' (excluded)' : ''}
                    </option>
                  );
                })}
              </select>
              {(staffFormData.role === 'bank_compliance_maker' || staffFormData.role === 'bank_compliance_checker') && (
                <p className="text-[10px] font-mono text-amber-600 mt-1 flex items-center gap-1">
                  <AlertTriangle className="w-3 h-3" />
                  Compliance Maker and Checker roles cannot be assigned to the same user.
                </p>
              )}
            </div>
            <div>
              <label className="block text-[10px] font-mono font-medium text-zinc-500 uppercase tracking-wider mb-1">Department</label>
              <select
                value={staffFormData.department}
                onChange={(e) => setStaffFormData({ ...staffFormData, department: e.target.value })}
                className="w-full px-3 py-2 border border-zinc-200 rounded-none font-mono text-xs bg-white"
              >
                <option value="Compliance">Compliance</option>
                <option value="Treasury">Treasury</option>
                <option value="Capital Markets">Capital Markets</option>
                <option value="Operations">Operations</option>
              </select>
            </div>
          </div>
          <div className="flex gap-3 pt-4">
            <button
              type="button"
              onClick={() => setShowAddStaffModal(false)}
              className="flex-1 px-4 py-2 border border-zinc-200 rounded-none font-mono text-xs uppercase tracking-wider hover:bg-zinc-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="flex-1 px-4 py-2 bg-black text-white rounded-none font-mono text-xs uppercase tracking-wider hover:bg-zinc-800"
            >
              Add User
            </button>
          </div>
        </form>
      </Modal>

      {/* Edit Staff Modal */}
      <Modal isOpen={showEditStaffModal} onClose={() => { setShowEditStaffModal(false); setSelectedStaff(null); }} title="Edit User">
        <form onSubmit={handleEditStaff} className="space-y-4">
          <div>
            <label className="block text-[10px] font-mono font-medium text-zinc-500 uppercase tracking-wider mb-1">Full Name</label>
            <input
              type="text"
              required
              value={staffFormData.name}
              onChange={(e) => setStaffFormData({ ...staffFormData, name: e.target.value })}
              className="w-full px-3 py-2 border border-zinc-200 rounded-none font-mono text-xs"
            />
          </div>
          <div>
            <label className="block text-[10px] font-mono font-medium text-zinc-500 uppercase tracking-wider mb-1">Email</label>
            <input
              type="email"
              required
              value={staffFormData.email}
              onChange={(e) => setStaffFormData({ ...staffFormData, email: e.target.value })}
              className="w-full px-3 py-2 border border-zinc-200 rounded-none font-mono text-xs"
            />
          </div>
          <div>
            <label className="block text-[10px] font-mono font-medium text-zinc-500 uppercase tracking-wider mb-1">Phone</label>
            <input
              type="tel"
              value={staffFormData.phone}
              onChange={(e) => setStaffFormData({ ...staffFormData, phone: e.target.value })}
              className="w-full px-3 py-2 border border-zinc-200 rounded-none font-mono text-xs"
            />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-[10px] font-mono font-medium text-zinc-500 uppercase tracking-wider mb-1">Role</label>
              <select
                value={staffFormData.role}
                onChange={(e) => setStaffFormData({ ...staffFormData, role: e.target.value })}
                className="w-full px-3 py-2 border border-zinc-200 rounded-none font-mono text-xs bg-white"
              >
                {AVAILABLE_ROLES.map(role => {
                  const disabled = getDisabledRole(staffFormData.role) === role;
                  return (
                    <option key={role} value={role} disabled={disabled}>
                      {getRoleLabel(role)}{disabled ? ' (excluded)' : ''}
                    </option>
                  );
                })}
              </select>
              {(staffFormData.role === 'bank_compliance_maker' || staffFormData.role === 'bank_compliance_checker') && (
                <p className="text-[10px] font-mono text-amber-600 mt-1 flex items-center gap-1">
                  <AlertTriangle className="w-3 h-3" />
                  Compliance Maker and Checker roles cannot be assigned to the same user.
                </p>
              )}
            </div>
            <div>
              <label className="block text-[10px] font-mono font-medium text-zinc-500 uppercase tracking-wider mb-1">Department</label>
              <select
                value={staffFormData.department}
                onChange={(e) => setStaffFormData({ ...staffFormData, department: e.target.value })}
                className="w-full px-3 py-2 border border-zinc-200 rounded-none font-mono text-xs bg-white"
              >
                <option value="Compliance">Compliance</option>
                <option value="Treasury">Treasury</option>
                <option value="Capital Markets">Capital Markets</option>
                <option value="Operations">Operations</option>
              </select>
            </div>
          </div>
          <div className="flex gap-3 pt-4">
            <button
              type="button"
              onClick={() => { setShowEditStaffModal(false); setSelectedStaff(null); }}
              className="flex-1 px-4 py-2 border border-zinc-200 rounded-none font-mono text-xs uppercase tracking-wider hover:bg-zinc-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="flex-1 px-4 py-2 bg-black text-white rounded-none font-mono text-xs uppercase tracking-wider hover:bg-zinc-800"
            >
              Save Changes
            </button>
          </div>
        </form>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal isOpen={showDeleteConfirmModal} onClose={() => { setShowDeleteConfirmModal(false); setSelectedStaff(null); }} title="Delete User" size="sm">
        <div className="text-center">
          <div className="w-16 h-16 bg-red-100 flex items-center justify-center mx-auto mb-4">
            <Trash2 className="w-8 h-8 text-red-600" />
          </div>
          <p className="text-xs font-mono text-zinc-600 mb-2">Are you sure you want to delete</p>
          <p className="font-mono text-sm font-semibold text-zinc-900 mb-4">{selectedStaff?.name}?</p>
          <p className="text-[10px] font-mono text-zinc-500 mb-6">This action cannot be undone.</p>
          <div className="flex gap-3">
            <button
              onClick={() => { setShowDeleteConfirmModal(false); setSelectedStaff(null); }}
              className="flex-1 px-4 py-2 border border-zinc-200 rounded-none font-mono text-xs uppercase tracking-wider hover:bg-zinc-50"
            >
              Cancel
            </button>
            <button
              onClick={handleDeleteStaff}
              className="flex-1 px-4 py-2 bg-red-600 text-white rounded-none font-mono text-xs uppercase tracking-wider hover:bg-red-700"
            >
              Delete
            </button>
          </div>
        </div>
      </Modal>

      {/* Staff Details Modal */}
      <Modal isOpen={showStaffDetailsModal} onClose={() => { setShowStaffDetailsModal(false); setSelectedStaff(null); }} title="User Details">
        {selectedStaff && (
          <div className="space-y-6">
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 bg-zinc-200 rounded-none flex items-center justify-center">
                <span className="text-zinc-600 font-mono font-bold text-lg">{selectedStaff.name.split(' ').map(n => n[0]).join('')}</span>
              </div>
              <div className="flex-1">
                <h3 className="text-sm font-mono font-bold text-zinc-900">{selectedStaff.name}</h3>
                <p className="text-xs font-mono text-zinc-500">{selectedStaff.email}</p>
              </div>
              <StatusBadge status={selectedStaff.status} />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="p-3 border border-zinc-200">
                <p className="text-[10px] font-mono text-zinc-500 uppercase tracking-wider">Role</p>
                <span className={`inline-block mt-1 px-2 py-0.5 text-[10px] font-mono uppercase tracking-wider rounded-none ${getRoleColor(selectedStaff.role)}`}>
                  {getRoleLabel(selectedStaff.role)}
                </span>
              </div>
              <div className="p-3 border border-zinc-200">
                <p className="text-[10px] font-mono text-zinc-500 uppercase tracking-wider">Department</p>
                <p className="font-mono text-xs font-medium text-zinc-900 mt-1">{selectedStaff.department}</p>
              </div>
              <div className="p-3 border border-zinc-200">
                <p className="text-[10px] font-mono text-zinc-500 uppercase tracking-wider">Phone</p>
                <p className="font-mono text-xs font-medium text-zinc-900 mt-1">{selectedStaff.phone || 'Not set'}</p>
              </div>
              <div className="p-3 border border-zinc-200">
                <p className="text-[10px] font-mono text-zinc-500 uppercase tracking-wider">Joined</p>
                <p className="font-mono text-xs font-medium text-zinc-900 mt-1">{formatDate(selectedStaff.createdAt)}</p>
              </div>
            </div>
            <div className="p-3 border border-zinc-200">
              <p className="text-[10px] font-mono text-zinc-500 uppercase tracking-wider mb-1">Last Login</p>
              <p className="font-mono text-xs font-medium text-zinc-900">{formatTime(selectedStaff.lastLogin)}</p>
            </div>
            {selectedStaff.permissions && selectedStaff.permissions.length > 0 && (
              <div>
                <p className="text-[10px] font-mono text-zinc-500 uppercase tracking-wider mb-2">Permissions</p>
                <div className="flex flex-wrap gap-2">
                  {selectedStaff.permissions.map(perm => (
                    <span key={perm} className="px-2 py-0.5 bg-zinc-100 text-zinc-700 text-[10px] font-mono uppercase tracking-wider border border-zinc-200">{perm}</span>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </Modal>
    </div>
  );
};

export default BankAdminDashboard;
